import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { ModalTitle } from '@components/modal-title/modal-title';
import { SectionHeader } from '@components/section-header/section-header';
import type { CompanySatKey } from '@components/company-sat-cascader/company-sat-cascader';
import { ProspectService } from '@services/prospect-service';
import { CatalogService } from '@services/catalog-service';
import { CatalogService as CatalogCoreService } from '@intelishs-group/catalog-core';
import { NotifyService } from '@intelishs-group/notify-service';
import type {
  VEtapaProspectoDTO,
  Prospect,
  MovClienteProspectoDetailResponse,
} from '@schemas/prospect';
import type { GenericCatalog } from '@schemas/billing';

type LabelValue = { label: string; value: string };

@Component({
  selector: 'app-acceptance-preview-modal',
  standalone: true,
  imports: [
    CommonModule,
    NzModalModule,
    NzSpinModule,
    NzStepsModule,
    NzButtonModule,
    NzTagModule,
    NzAlertModule,
    ModalTitle,
    SectionHeader,
  ],
  templateUrl: './acceptance-preview-modal.html',
  styleUrl: './acceptance-preview-modal.css',
})
export class AcceptancePreviewModal implements OnChanges {
  private prospectService = inject(ProspectService);
  private catalogService = inject(CatalogService);
  private catalogCore = inject(CatalogCoreService);
  private notifyService = inject(NotifyService);

  @Input() visible = false;
  @Input() prospect: VEtapaProspectoDTO | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  loading = false;
  currentStep = 0;
  readonly totalSteps = 3;

  datosGenerales: LabelValue[] = [];
  domicilioFiscal: LabelValue[] = [];
  personalResponsable: LabelValue[] = [];
  regimenesLabels: string[] = [];
  fechaInicio: Date | null = null;
  descripcionActividades: string | null = null;
  empresasConConceptos: { empresa: string; conceptos: string[] }[] = [];

  condicionesGenerales: LabelValue[] = [];
  beneficioAdicional: string | null = null;
  observaciones: string | null = null;

  brokerLabel = '—';
  implantLabel = '—';
  condicionesInternas: string | null = null;
  observacionesComisiones: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.prospect?.fkIdProspecto) {
      this.resetState();
      this.loadData(this.prospect.fkIdProspecto);
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  nextStep(): void {
    if (this.currentStep < this.totalSteps - 1) this.currentStep++;
  }

  prevStep(): void {
    if (this.currentStep > 0) this.currentStep--;
  }

  private resetState(): void {
    this.currentStep = 0;
    this.datosGenerales = [];
    this.domicilioFiscal = [];
    this.personalResponsable = [];
    this.regimenesLabels = [];
    this.fechaInicio = null;
    this.descripcionActividades = null;
    this.empresasConConceptos = [];
    this.condicionesGenerales = [];
    this.beneficioAdicional = null;
    this.observaciones = null;
    this.brokerLabel = '—';
    this.implantLabel = '—';
    this.condicionesInternas = null;
    this.observacionesComisiones = null;
  }

  private async loadData(idProspecto: number): Promise<void> {
    this.loading = true;
    try {
      const [detail, prospect, serviceTypes, salaryTypes, periodicities, benefits, taxRegimes, brokers, inPlans, companySatKeys] =
        await Promise.all([
          this.prospectService.clienteProspecto_ByProspectoId(idProspecto),
          this.prospectService.byId(idProspecto),
          this.catalogService.serviceTypes(),
          this.catalogService.salaryTypes(),
          this.catalogService.periodicities(),
          this.catalogService.additionalBenefits(),
          this.catalogCore.getFiscalRegimes(),
          this.catalogCore.getBrokers(),
          this.catalogCore.getInPlans(),
          this.catalogService.companySatKeys(),
        ]);

      const d = (detail ?? null) as MovClienteProspectoDetailResponse | null;
      const p = (prospect ?? null) as Prospect | null;
      const cats = {
        services: (serviceTypes ?? []) as GenericCatalog[],
        salaries: (salaryTypes ?? []) as GenericCatalog[],
        periods: (periodicities ?? []) as GenericCatalog[],
        benefits: (benefits ?? []) as GenericCatalog[],
        taxRegimes: (taxRegimes ?? []) as GenericCatalog[],
        brokers: (brokers ?? []) as GenericCatalog[],
        inPlans: (inPlans ?? []) as GenericCatalog[],
      };
      const companies = (companySatKeys ?? []) as unknown as CompanySatKey[];

      this.buildDatosGenerales(d, p, cats.taxRegimes);
      this.buildDomicilioFiscal(d);
      this.buildPersonalResponsable(p);
      this.buildDescripcionActividades(d);
      this.buildEmpresasConConceptos(d, companies);
      this.buildCondicionesGenerales(d, cats);
      this.beneficioAdicional = this.resolveDescription(cats.benefits, d?.idBeneficioAdicional);
      this.observaciones = this.trimOrNull(d?.datosJson?.sobservaciones);
      this.buildBrokerImplant(p, cats.brokers, cats.inPlans);
      this.condicionesInternas = this.trimOrNull(d?.datosJson?.scondicionesinternas);
      this.observacionesComisiones = this.trimOrNull(d?.datosJson?.sobservacionescomisiones);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cargar la vista previa del cliente.';
      this.notifyService.errorNotyfy(msg);
      this.close();
    } finally {
      this.loading = false;
    }
  }

  private buildDatosGenerales(
    d: MovClienteProspectoDetailResponse | null,
    p: Prospect | null,
    taxRegimes: GenericCatalog[],
  ): void {
    const summary = d?.prospect;
    const info = p?.datosJson?.prospecto;
    this.datosGenerales = [
      { label: 'Nombre o Razón Social', value: this.fmt(summary?.nombre ?? p?.nombre) },
      { label: 'R.F.C.', value: this.fmt(summary?.rfc ?? p?.rfc) },
      { label: 'Nombre Comercial', value: this.fmt(summary?.nombreComercial ?? info?.snombrecomercial) },
      { label: 'Giro / Actividad', value: this.fmt(summary?.actividad ?? info?.sactividad) },
      { label: 'Núm. Aprox. Empleados', value: this.fmt(info?.iempleados) },
    ];
    const regimenIds = summary?.regimenesFiscales ?? info?.regimenesfiscales ?? [];
    this.regimenesLabels = regimenIds
      .map((id) => this.resolveLabel(taxRegimes, id))
      .filter((l): l is string => !!l);
  }

  private buildDomicilioFiscal(d: MovClienteProspectoDetailResponse | null): void {
    const s = d?.prospect;
    this.domicilioFiscal = [
      { label: 'Calle', value: this.fmt(s?.direccion) },
      { label: 'Núm. Exterior', value: this.fmt(s?.numeroExterior) },
      { label: 'Núm. Interior', value: this.fmt(s?.numeroInterior) },
      { label: 'Colonia', value: this.fmt(s?.colonia) },
      { label: 'Delegación / Municipio', value: this.fmt(s?.municipio) },
      { label: 'Estado', value: this.fmt(s?.localidad) },
      { label: 'C.P.', value: this.fmt(s?.codigoPostal) },
    ];
  }

  private buildPersonalResponsable(p: Prospect | null): void {
    const c = p?.datosJson?.contacto;
    const area = (c as { sarea?: string | null } | undefined)?.sarea;
    this.personalResponsable = [
      { label: 'Nombre', value: this.fmt(c?.snombre) },
      { label: 'Correo electrónico', value: this.fmt(c?.scorreo) },
      { label: 'Teléfono', value: this.fmt(c?.itelefono) },
      { label: 'Área', value: this.fmt(area) },
    ];
  }

  private buildDescripcionActividades(d: MovClienteProspectoDetailResponse | null): void {
    const iso = d?.datosJson?.dfechainicio;
    this.fechaInicio = iso ? new Date(iso) : null;
    this.descripcionActividades = this.trimOrNull(d?.datosJson?.scomentarioactividades ?? d?.comentario);
  }

  private buildEmpresasConConceptos(
    d: MovClienteProspectoDetailResponse | null,
    companies: CompanySatKey[],
  ): void {
    this.empresasConConceptos = (d?.datosJson?.aempresaspagadoras ?? []).map((e) => {
      const company = companies.find((c) => c.companyId === e.iidempresavendedora);
      return {
        empresa: e.snombreempresavendedora ?? company?.name ?? `#${e.iidempresavendedora ?? ''}`,
        conceptos: (e.aconceptosvalidos ?? [])
          .map((c) => this.resolveConceptoLabel(c.sclaveprodserv, company))
          .filter((v) => v.length > 0),
      };
    });
  }

  private resolveConceptoLabel(
    clave: string | null | undefined,
    company: CompanySatKey | undefined,
  ): string {
    const code = (clave ?? '').trim();
    if (!code) return '';
    const match = company?.satKeys?.find((k) => String(k.satClassId) === code);
    if (!match) return code;
    const desc = (match.subClass ?? match.alias ?? '').trim();
    return desc ? `${code} - ${desc}` : code;
  }

  private buildCondicionesGenerales(
    d: MovClienteProspectoDetailResponse | null,
    cats: {
      services: GenericCatalog[];
      salaries: GenericCatalog[];
      periods: GenericCatalog[];
    },
  ): void {
    const periodLabel = this.resolveLabel(cats.periods, d?.idPeriodicidad);
    const otraPeriodicidad = (d?.otraPeriodicidad ?? '').trim();
    const otraId = cats.periods.find((p) => (p.description ?? '').trim().toLowerCase() === 'otro')?.id;
    const periodValue =
      d?.idPeriodicidad != null && otraId != null && d.idPeriodicidad === otraId && otraPeriodicidad
        ? `${periodLabel ?? 'Otro'} — ${otraPeriodicidad}`
        : periodLabel ?? '—';

    this.condicionesGenerales = [
      { label: 'Tipo de Servicio', value: this.resolveLabel(cats.services, d?.idTipoServicio) ?? '—' },
      { label: 'Tipo de Salarios', value: this.resolveLabel(cats.salaries, d?.idTipoSalario) ?? '—' },
      { label: 'Reconoce Antigüedad', value: d?.reconoceAntiguedad == null ? '—' : d.reconoceAntiguedad ? 'Sí' : 'No' },
      { label: 'Periodicidad', value: periodValue },
      { label: '% Comisión', value: d?.porcentajeComision == null ? '—' : `${d.porcentajeComision.toFixed(2)}%` },
      { label: 'IVA', value: d?.porcentajeIva == null ? '—' : `${d.porcentajeIva.toFixed(2)}%` },
      {
        label: 'Periodicidad Facturación',
        value: this.resolveLabel(cats.periods, d?.idPeriodicidadFacturacion) ?? '—',
      },
    ];
  }

  private buildBrokerImplant(p: Prospect | null, brokers: GenericCatalog[], inPlans: GenericCatalog[]): void {
    const v = p?.datosJson?.vendedores?.[0];
    this.brokerLabel = this.resolveLabel(brokers, v?.fkidbroker) ?? '—';
    this.implantLabel = this.resolveLabel(inPlans, v?.fkidimplant) ?? '—';
  }

  private resolveLabel(catalog: GenericCatalog[], id: number | null | undefined): string | null {
    if (id == null) return null;
    const match = catalog.find((c) => c.id === id);
    if (!match) return `${id}`;
    const desc = (match.description ?? '').trim();
    return desc ? `${id} - ${desc}` : `${id}`;
  }

  private resolveDescription(catalog: GenericCatalog[], id: number | null | undefined): string | null {
    if (id == null) return null;
    const match = catalog.find((c) => c.id === id);
    const desc = (match?.description ?? '').trim();
    return desc || `${id}`;
  }

  private fmt(v: unknown): string {
    if (v == null) return '—';
    const s = String(v).trim();
    return s.length ? s : '—';
  }

  private trimOrNull(v: string | null | undefined): string | null {
    if (v == null) return null;
    const s = v.trim();
    return s.length ? s : null;
  }
}
